<template>
  <!-- Auth loading -->
  <div v-if="isValidating" id="app-loading" class="auth-loading-screen">
    <div class="auth-loading-spinner"></div>
    <div class="auth-loading-text">Memverifikasi akses...</div>
  </div>

  <!-- Access denied -->
  <AccessDenied v-else-if="authError" :message="authError" />

  <!-- Main app -->
  <template v-else-if="user">
    <button class="sidebar-toggle" id="toggleLeft" @click="togglePanel('left')">☰</button>
    <button class="sidebar-toggle" id="toggleRight" @click="togglePanel('right')">🧠</button>
    <div class="panel-backdrop" :class="{ show: panelBackdropShow }" @click="closeAllPanels"></div>

    <div id="app-main" class="app">
      <PanelLeft
        :class="{ open: leftOpen }"
        @close-panels="closeAllPanels"
      />

      <PanelCenter
        @open-compare="handleOpenCompare"
      />

      <PanelRight :class="{ open: rightOpen }" />
    </div>

    <MobileNav @navigate="mobileShow" :active-panel="mobileActive" />

    <DetailModal />
    <CompareModal />
    <DevModeOverlay />
  </template>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import PanelLeft from './components/PanelLeft.vue'
import PanelCenter from './components/PanelCenter.vue'
import PanelRight from './components/PanelRight.vue'
import MobileNav from './components/MobileNav.vue'
import DetailModal from './components/DetailModal.vue'
import CompareModal from './components/CompareModal.vue'
import DevModeOverlay from './components/DevModeOverlay.vue'
import AccessDenied from './components/AccessDenied.vue'
import { useDetailModal } from './composables/useDetailModal'
import { useCompare } from './composables/useCompare'
import { useTimeline } from './composables/useTimeline'
import { useDevMode } from './composables/useDevMode'
import { useWorkflowData } from './composables/useWorkflowData'
import { useAuth } from './composables/useAuth'
import { useActiveReport } from './composables/useActiveReport'
import { DEPARTMENT_TABS } from './data/constants'

const { closeDetail, isOpen: detailOpen } = useDetailModal()
const { closeCompare, compareOpen } = useCompare()
const { selectedDate, setAvailableDates } = useTimeline()
const { devMode, handleMouseMove, handleClick, handleKeydown: devKeydown } = useDevMode()
const { fetchAvailableDates, availableDates, datesWithAnalysis } = useWorkflowData()
const { user, isValidating, authError, validateFromUrl } = useAuth()
const { activeDept, switchDept } = useActiveReport()

const leftOpen = ref(false)
const rightOpen = ref(false)
const panelBackdropShow = ref(false)
const mobileActive = ref('center')

function togglePanel(side) {
  const isLeft = side === 'left'
  const wasOpen = isLeft ? leftOpen.value : rightOpen.value
  closeAllPanels()
  if (!wasOpen) {
    if (isLeft) leftOpen.value = true
    else rightOpen.value = true
    panelBackdropShow.value = true
  }
}

function closeAllPanels() {
  leftOpen.value = false
  rightOpen.value = false
  panelBackdropShow.value = false
}

function mobileShow(panel) {
  mobileActive.value = panel
  closeAllPanels()
  if (panel !== 'center') {
    if (panel === 'left') leftOpen.value = true
    else rightOpen.value = true
    panelBackdropShow.value = true
  }
}

function handleOpenCompare() {
  const { openCompare } = useCompare()
  openCompare(selectedDate.value)
}

function onKeydown(e) {
  devKeydown(e)
  if (e.key === 'Escape') {
    closeDetail()
    closeCompare()
    closeAllPanels()
  }
}

onMounted(async () => {
  document.addEventListener('keydown', onKeydown)
  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('click', handleClick, true)

  // Validate token from URL first
  await validateFromUrl()

  if (user.value) {
    // Auto-redirect to first allowed dept if current dept is not accessible
    const currentDeptId = DEPARTMENT_TABS[activeDept.value]?.id
    if (currentDeptId && !user.value.allowedDepts.includes(currentDeptId)) {
      const firstAllowedIdx = DEPARTMENT_TABS.findIndex(d => user.value.allowedDepts.includes(d.id))
      if (firstAllowedIdx >= 0) switchDept(firstAllowedIdx)
    }

    // Then load workflow data
    fetchAvailableDates().then(() => {
      setAvailableDates(availableDates.value, datesWithAnalysis.value)
    })
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown)
  document.removeEventListener('mousemove', handleMouseMove)
  document.removeEventListener('click', handleClick, true)
})
</script>

<style scoped>
.auth-loading-screen {
  position: fixed;
  inset: 0;
  background: #0d0d0d;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  z-index: 9999;
}

.auth-loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #2a2a2a;
  border-top-color: #22c55e;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.auth-loading-text {
  font-size: 13px;
  color: #555;
}
</style>
